import React, { useRef, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import sizeMe from 'react-sizeme';
import { scaleLinear as D3ScaleLinear } from 'd3';

import Tooltip from '../styled/tooltip';
import { teal } from '../../data/colors';

const progMarker = 26;
const flatProgMarker = 5;

const useStyles = makeStyles(theme => ({
    root: {
    },
    progressBar: {
        height: 20,
        width: '100%',
        background: `${alpha(theme.palette.text.primary, 0.15)}`,
        borderRadius: 3,
    },
    progressBarFill: {
        background: teal[600],
        borderRadius: 5,
        height: 20,
    },
    progressCurrent: {
        width: progMarker,
        height: progMarker,
        background: teal[400],
        marginTop: -23,
        borderRadius: 15,
        border: `2px solid ${teal[500]}`,
    },
    floatRight: {
        float: 'right',
        marginRight: 10,
        // paddingTop: 4,
    },
    // flat styles
    flatProgressBar: {
        height: 35,
        width: '100%',
        background: `${alpha(theme.palette.text.primary, 0.15)}`,
    },
    flatProgressBarFill: {
        top: 7,
        position: 'relative',
        height: 20,
        left: 2,
    },
    flatProgressCurrent: {
        width: flatProgMarker,
        height: 40,
        background: teal[400],
        position: 'relative',
        top: -23,
    },
    flatDescription: {
        fontFamily: 'Roboto',
        fontWeight: 500,
        fontSize: 12,
        letterSpacing: '0.2em',
        fontVariant: 'all-petite-caps',
        color: theme.palette.text.primary,

        '&.end': {
            display: 'inline-block',
            float: 'right',
        }
    },
    flatAxis: {
        position: 'sticky',
        overflow: 'hidden',
        '&.label': {
            fontFamily: 'Roboto',
            fontWeight: 500,
            fontVariant: 'all-petite-caps',
            fontSize: 12,
            color: theme.palette.text.primary,
        },
    },
}));

const BulletChart = sizeMe({ monitorHeight: true })((props) => {
    const ref = useRef(null);
    const classes = useStyles();

    const [units, setUnits] = useState(0);
    const [pct, setPct] = useState(0);

    // value and size hook
    useEffect(() => {
        if (ref) {
            const valFn = D3ScaleLinear()
                .domain([props.min || 0, props.max || 100])
                .range([0, props.size.width || 100]);

            const units = valFn(props.value);
            const pct = props.pct ? props.value : 100 * props.value / (props.max || 1);
            setUnits(units);
            setPct(pct);
        }
    }, [props.value, props.pct, props.min, props.max, props.size]);

    return (
        <Tooltip title={`${pct.toFixed(2)}% ${props.label}`}>
            <div>
                <div className={classes.clearFix}>
                    <div className={classes.floatRight}>{props.label}</div>
                </div>
                <div className={classes.progressBar}>
                    <div
                        className={classes.progressBarFill}
                        style={{ width: `${pct}%` }}>
                    </div>
                    <div className={classes.progressCurrent}
                        style={{
                            marginLeft: (units > progMarker / 2)
                                ? units - (progMarker / 2)
                                : units,
                            display: props.max ? '' : 'none'
                        }}>
                    </div>
                </div>
            </div>
        </Tooltip>
    );
});

BulletChart.propTypes = {
    value: PropTypes.number,
    pct: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    label: PropTypes.string,

    // provided by sizeme
    size: PropTypes.object,
};

const FlatBulletChart = sizeMe({ monitorHeight: true })((props) => {
    const ref = useRef(null);
    const classes = useStyles();

    const [threshold, setThreshold] = useState(0);
    const [pct, setPct] = useState(0);
    const [axisValues, setAxisValues] = useState([]);

    // value and size hook
    useEffect(() => {
        if (ref) {
            const valFn = D3ScaleLinear()
                .domain([props.min || 0, props.max || 100])
                .range([0, props.size.width || 100]);

            const pct = props.pct ? props.value : 100 * props.value / (props.max || 1);
            const axis = valFn.ticks(10).map(a => ({
                position: valFn(a),
                value: a,
            }));
            setThreshold(valFn(props.threshold));
            setPct(pct);
            setAxisValues(axis);
        }
    }, [props.value, props.pct, props.min, props.max, props.size, props.threshold]);

    return (
        <React.Fragment>
            {props.description &&
                <div>
                    <span className={classes.flatDescription}>
                        {props.description}
                    </span>
                    <span className={clsx(classes.flatDescription, 'end')} >
                        max
                    </span>
                </div>
            }
            <div className={classes.flatProgressBar}>
                <Tooltip title={`Value: ${pct.toFixed(2)}`}>
                    <div
                        className={classes.flatProgressBarFill}
                        style={{ width: `${pct}%`, background: props.color || teal[600] }}>
                    </div>
                </Tooltip>
                <Tooltip title={`Threshold: ${props.threshold.toFixed(2)}`}>
                    <div className={classes.flatProgressCurrent}
                        style={{
                            left: (threshold > flatProgMarker / 2)
                                ? threshold - (flatProgMarker / 2)
                                : threshold,
                            display: props.threshold ? '' : 'none'
                        }}>
                    </div>
                </Tooltip>
            </div>
            {props.axis &&
                <div className={classes.flatAxis}>
                    {axisValues.map(a => (
                        <span
                            key={a.value}
                            className={clsx(classes.flatAxis, 'label')}
                            style={{ left: a.position }}>
                            {a.value}
                        </span>
                    ))}
                </div>
            }
        </React.Fragment>
    );
});

FlatBulletChart.propTypes = {
    value: PropTypes.number.isRequired,

    // optional
    threshold: PropTypes.number,
    description: PropTypes.string,
    axis: PropTypes.bool,
    pct: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,

    // provided by sizeme
    size: PropTypes.object,
};

export {
    BulletChart as default, // for use in alert/mtdt list
    FlatBulletChart, // ML tab
};
